import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  OnDestroy,
  Renderer2,
  ElementRef,
  ViewChild,
} from "@angular/core";

import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
} from "@angular/forms";

import { Subject, Subscription } from "rxjs";
import { auditTime, takeUntil } from "rxjs/operators";

import { EventService } from "src/app/core/services/event.service";
import { PermissionsService } from "src/app/core/services/permissions.service";
import { PropertyDataService } from "src/app/core/services/property-data.service";
import { PropertyValidationRulesService } from "src/app/core/services/propertyValidationRules.service";

@Component({
  selector: "app-areas-nearby",
  templateUrl: "./areas-nearby.component.html",
  styleUrls: ["./areas-nearby.component.scss"],
})
export class AreasNearbyComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() propertyAreasNearbyData: any;
  @Input() submitted: boolean = false;
  @Input() dataToFormInit: any;
  @Input() detailsForm: any;
  @Input() dataToFormInitDetails: any;
  @Input() property: any;

  subscriptionSubmitted!: Subscription;
  aiExtractedSub?: Subscription;
  selectValueNumber = Array.from({ length: 300 }, (_, i) => i + 1);
  propertyAreasNearbyForm!: FormGroup;
  isBuildingImplantationArea = false;

  @ViewChild("formElementRef") formElementRef!: ElementRef;

  private destroy$ = new Subject<void>();

  constructor(
    private eventService: EventService,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    private permissionsService: PermissionsService,
    private propertyData: PropertyDataService,
    private propertyValidationService: PropertyValidationRulesService,
  ) { }

  ngOnInit(): void {
    const propertyType = this.dataToFormInitDetails?.propertyTypes.find(
      (s: any) =>
        s.id === this.detailsForm?.data?.propertyType && s?.groupMainDescription === "Land",
    );
    if (propertyType) {
      this.isBuildingImplantationArea = true;
    }

    this.initFormValidation();

    this.propertyValidationService
      .rules$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((map) => {
        const isApartmentOrHouse = this.dataToFormInitDetails?.propertyTypes.find(
          (s: any) => {
            const idToCompare = this.detailsForm?.data?.propertyType2 ? this.detailsForm?.data?.propertyType2 : this.detailsForm?.data?.propertyType;
            return s.id === idToCompare && (s?.groupMainDescription === "Apartments" || s?.groupMainDescription === "Houses" || s?.groupMainDescription === "---")
          },
        )
        if (!map?.size || !this.propertyAreasNearbyForm || !isApartmentOrHouse) return;

        const revalidateTree = (ctrl: AbstractControl) => {
          if (ctrl instanceof FormGroup) {
            Object.values(ctrl.controls).forEach(revalidateTree);
          }

          ctrl.updateValueAndValidity({ emitEvent: false });
        };

        revalidateTree(this.propertyAreasNearbyForm);
      });

    // CMI
    this.propertyAreasNearbyForm.valueChanges
      .pipe(auditTime(150))
      .subscribe((val) => this.propertyData.updateTab("areasNearby", val));

    this.propertyData.updateTab(
      "areasNearby",
      this.propertyAreasNearbyForm.getRawValue(),
    );
  }

  ngAfterViewInit(): void {
    this.subscriptionSubmitted = this.eventService.subscribe(
      "get-data-tab-areas-nearby",
      () => {
        this.propertyAreasNearbyData.data = this.propertyAreasNearbyForm.value;
        this.propertyAreasNearbyData.errors = this.getFormErrors();
        this.propertyAreasNearbyData.valid = this.propertyAreasNearbyForm.valid;
        this.eventService.broadcast("check-warning-add-property");
      },
    );

    this.propertyAreasNearbyForm.valueChanges
      .pipe(auditTime(150), takeUntil(this.destroy$))
      ?.subscribe(() => {
        this.propertyAreasNearbyData.data = this.propertyAreasNearbyForm.value;
        this.propertyAreasNearbyData.errors = this.getFormErrors();
        this.propertyAreasNearbyData.valid = this.propertyAreasNearbyForm.valid;
        this.eventService.broadcast("check-warning-add-property");
      });

    this.submitted = true;
    this.setConfig();

    this.aiExtractedSub = this.eventService.subscribe("ai-doc-extracted", (data: any) => {
      this.applyAiExtraction(data);
    });
  }

  private applyAiExtraction(data: any) {
    if (!data || !this.propertyAreasNearbyForm) return;

    const patch: any = {};
    const animatedFields: string[] = [];

    if (data.usefulArea !== undefined && data.usefulArea !== null) {
      patch.usefulArea = data.usefulArea;
      animatedFields.push("usefulArea");
    }
    if (data.grossArea !== undefined && data.grossArea !== null) {
      patch.grossArea = data.grossArea;
      animatedFields.push("grossArea");
    }
    if (data.grossPrivateArea !== undefined && data.grossPrivateArea !== null) {
      patch.grossPrivateArea = data.grossPrivateArea;
      animatedFields.push("grossPrivateArea");
    }
    if (data.grossDependentArea !== undefined && data.grossDependentArea !== null) {
      patch.grossDependentArea = data.grossDependentArea;
      animatedFields.push("grossDependentArea");
    }
    if (data.landArea !== undefined && data.landArea !== null) {
      patch.landArea = data.landArea;
      animatedFields.push("landArea");
    }
    if (data.buildingImplantationArea !== undefined && data.buildingImplantationArea !== null) {
      patch.buildingImplantationArea = data.buildingImplantationArea;
      animatedFields.push("buildingImplantationArea");
    }

    const rooms = this.toPositiveInt(data.rooms);
    const bathrooms = this.toPositiveInt(data.bathrooms);
    const livingRooms = this.toPositiveInt(data.livingRooms);
    const offices = this.toPositiveInt(data.offices);
    const suites = this.toPositiveInt(data.suites);
    const parkingSpots = this.toPositiveInt(data.parkingSpots);
    this.applyDivisionCountPatch(patch, animatedFields, rooms, [
      "quarto",
      "bedroom",
      "room",
    ]);
    this.applyDivisionCountPatch(patch, animatedFields, bathrooms, [
      "casa de banho",
      "wc",
      "banho",
      "bath",
    ]);
    this.applyDivisionCountPatch(patch, animatedFields, livingRooms, [
      "sala",
      "salas",
      "living room",
      "lounge",
    ]);
    this.applyDivisionCountPatch(patch, animatedFields, offices, [
      "escritorio",
      "office",
      "gabinete",
    ]);
    this.applyDivisionCountPatch(patch, animatedFields, suites, [
      "suite",
      "suíte",
    ]);
    this.applyDivisionCountPatch(patch, animatedFields, parkingSpots, [
      "garagem",
      "estacionamento",
      "parking",
      "lugar",
    ]);
    this.applyDivisionDetailsPatch(patch, animatedFields, data?.divisions);
    this.applyProximitiesPatch(patch, animatedFields, data?.proximities);

    if (Object.keys(patch).length === 0) return;

    this.propertyAreasNearbyForm.patchValue(patch);
    this.propertyAreasNearbyData.data = this.propertyAreasNearbyForm.value;
    this.propertyAreasNearbyData.errors = this.getFormErrors();
    this.propertyAreasNearbyData.valid = this.propertyAreasNearbyForm.valid;
    this.eventService.broadcast("check-warning-add-property");

    this.highlightAiFields(animatedFields);
  }

  private applyDivisionCountPatch(
    patch: Record<string, any>,
    animatedFields: string[],
    value: number | undefined,
    keywords: string[],
  ) {
    if (!value) return;
    const divisionId = this.findDivisionIdByKeywords(keywords);
    if (!divisionId) return;
    const controlName = `division${divisionId}`;
    patch[controlName] = value;
    animatedFields.push(controlName);
  }

  private applyDivisionDetailsPatch(
    patch: Record<string, any>,
    animatedFields: string[],
    divisions: any,
  ) {
    if (!Array.isArray(divisions)) return;

    for (const item of divisions) {
      const label = this.normalizeDivisionLabel(item?.label ?? item?.name);
      if (!label) continue;

      const division = (this.dataToFormInit?.divisions ?? []).find((entry: any) => {
        const entryLabel = this.normalizeDivisionLabel(
          entry?.description ?? entry?.division?.description ?? "",
        );
        return !!entryLabel && (entryLabel.includes(label) || label.includes(entryLabel));
      });
      const divisionId = Number(division?.division?.id ?? 0);
      if (!divisionId) continue;

      const count = this.toPositiveInt(item?.count);
      if (!count) continue;

      const controlName = `division${divisionId}`;
      patch[controlName] = count;
      animatedFields.push(controlName);
    }
  }

  private applyProximitiesPatch(
    patch: Record<string, any>,
    animatedFields: string[],
    proximities: any,
  ) {
    if (!Array.isArray(proximities)) return;

    const nearbyEntries = this.dataToFormInit?.nearby ?? [];
    const units = this.dataToFormInit?.units ?? [];

    for (const item of proximities) {
      const label = this.normalizeDivisionLabel(item?.label ?? item?.name);
      if (!label) continue;

      const nearby = nearbyEntries.find((entry: any) => {
        const text = this.normalizeDivisionLabel(
          entry?.description ?? entry?.nearby?.description ?? "",
        );
        return !!text && (text.includes(label) || label.includes(text));
      });
      const nearbyId = Number(nearby?.nearby?.id ?? 0);
      if (!nearbyId) continue;

      patch[`nearby${nearbyId}`] = true;
      animatedFields.push(`nearby${nearbyId}`);

      const distance = Number(item?.distance);
      if (Number.isFinite(distance) && distance > 0) {
        patch[`valNearby-${nearbyId}`] = distance;
        animatedFields.push(`valNearby-${nearbyId}`);
      }

      const unitText = this.normalizeDivisionLabel(item?.unit);
      if (unitText) {
        const unit = units.find((entry: any) => {
          const text = this.normalizeDivisionLabel(entry?.description ?? "");
          return !!text && (text.includes(unitText) || unitText.includes(text));
        });
        if (unit?.id) {
          patch[`unitNearBy-${nearbyId}`] = unit.id;
          animatedFields.push(`unitNearBy-${nearbyId}`);
        }
      }
    }
  }

  private findDivisionIdByKeywords(keywords: string[]): number | undefined {
    const entries = this.dataToFormInit?.divisions ?? [];
    for (const entry of entries) {
      const label = this.normalizeDivisionLabel(
        entry?.description ?? entry?.division?.description ?? "",
      );
      if (!label) continue;
      const matched = keywords.some((keyword) =>
        label.includes(this.normalizeDivisionLabel(keyword)),
      );
      if (matched) return Number(entry?.division?.id ?? 0) || undefined;
    }
    return undefined;
  }

  private normalizeDivisionLabel(value: any): string {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[.,/\\-]/g, " ")
      .replace(/\s+/g, " ")
      .toLowerCase()
      .trim();
  }

  private toPositiveInt(value: any): number | undefined {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return undefined;
    return Math.floor(num);
  }

  private highlightAiFields(controlNames: string[]) {
    if (!this.formElementRef?.nativeElement) return;

    for (const name of controlNames) {
      const el = this.formElementRef.nativeElement.querySelector(`[formcontrolname="${name}"]`);
      if (!el) continue;
      this.renderer.addClass(el, "ai-field-fill");
      setTimeout(() => this.renderer.removeClass(el, "ai-field-fill"), 1600);
    }
  }

  /**
   * Form data get
   */
  get form() {
    return this.propertyAreasNearbyForm.controls;
  }
  /**
   * Form Validation
   */
  initFormValidation() {
    this.dynamicDataForm();

    this.propertyAreasNearbyForm = this.formBuilder.group(
      this.propertyAreasNearbyData.data,
    );

    this.applyDivisionsMinRule();
  }

  dynamicDataForm() {
    const warn = (ruleKey: string, control: (c: AbstractControl) => boolean) =>
      this.propertyValidationService.warningIfEnabled(ruleKey, control);

    if (
      this.propertyAreasNearbyData === undefined ||
      this.propertyAreasNearbyData === null
    )
      this.propertyAreasNearbyData = { data: {} };

    this.propertyAreasNearbyData.data[`usefulArea`] = [
      this.propertyAreasNearbyData?.data?.usefulArea ?? "",
      [warn("areasNearby.usefulArea", (c) => !c?.value)],
    ];

    this.propertyAreasNearbyData.data[`grossArea`] = [
      this.propertyAreasNearbyData?.data?.grossArea ?? "",
      [warn("areasNearby.grossArea", (c) => !c?.value)],
    ];
    this.propertyAreasNearbyData.data[`grossDependentArea`] = [
      this.propertyAreasNearbyData?.data?.grossDependentArea
        ? this.propertyAreasNearbyData?.data?.grossDependentArea
        : 0,
      [warn("areasNearby.grossDependentArea", (c) => !c?.value)],
    ];
    this.propertyAreasNearbyData.data[`grossPrivateArea`] = [
      this.propertyAreasNearbyData?.data?.grossPrivateArea
        ? this.propertyAreasNearbyData?.data?.grossPrivateArea
        : 0,
      [warn("areasNearby.grossPrivateArea", (c) => !c?.value)],
    ];
    this.propertyAreasNearbyData.data[`landArea`] = [
      this.propertyAreasNearbyData?.data?.landArea
        ? this.propertyAreasNearbyData?.data?.landArea
        : 0,
      [warn("areasNearby.landArea", (c) => !c?.value)],
    ];
    this.propertyAreasNearbyData.data[`buildingImplantationArea`] = [
      this.propertyAreasNearbyData?.data?.buildingImplantationArea
        ? this.propertyAreasNearbyData?.data?.buildingImplantationArea
        : 0,
    ];

    this.propertyAreasNearbyData.data[`hideUsefulArea`] = [
      this.propertyAreasNearbyData?.data?.hideUsefulArea === true
        ? this.propertyAreasNearbyData?.data?.hideUsefulArea
        : false,
    ];
    this.propertyAreasNearbyData.data[`hideGrossArea`] = [
      this.propertyAreasNearbyData?.data?.hideGrossArea === true
        ? this.propertyAreasNearbyData?.data?.hideGrossArea
        : false,
    ];
    this.propertyAreasNearbyData.data[`hideGrossDependentArea`] = [
      this.propertyAreasNearbyData?.data?.hideGrossDependentArea === true
        ? this.propertyAreasNearbyData?.data?.hideGrossDependentArea
        : false,
    ];
    this.propertyAreasNearbyData.data[`hideGrossPrivateArea`] = [
      this.propertyAreasNearbyData?.data?.hideGrossPrivateArea === true
        ? this.propertyAreasNearbyData?.data?.hideGrossPrivateArea
        : false,
    ];
    this.propertyAreasNearbyData.data[`hideLandArea`] = [
      this.propertyAreasNearbyData?.data?.hideLandArea === true
        ? this.propertyAreasNearbyData?.data?.hideLandArea
        : false,
    ];
    this.propertyAreasNearbyData.data[`hideBuildingImplantationArea`] = [
      this.propertyAreasNearbyData?.data?.hideBuildingImplantationArea === true
        ? this.propertyAreasNearbyData?.data?.hideBuildingImplantationArea
        : false,
    ];

    for (let d of this.dataToFormInit.divisions) {
      this.propertyAreasNearbyData.data[`division${d.division.id}`] = [
        this.propertyAreasNearbyData.data[`division${d.division.id}`]
          ? this.propertyAreasNearbyData.data[`division${d.division.id}`]
          : 0,
      ];

      this.propertyAreasNearbyData.data[`hideDivision${d.division.id}`] = [
        this.propertyAreasNearbyData.data[`hideDivision${d.division.id}`] ===
          true
          ? this.propertyAreasNearbyData.data[`hideDivision${d.division.id}`]
          : false,
      ];
    }

    for (let n of this.dataToFormInit.nearby) {
      this.propertyAreasNearbyData.data[`nearby${n.nearby.id}`] = [
        this.propertyAreasNearbyData.data[`nearby${n.nearby.id}`],
      ];

      this.propertyAreasNearbyData.data[`valNearby-${n.nearby.id}`] = [
        this.propertyAreasNearbyData.data[`valNearby-${n.nearby.id}`],
      ];

      this.propertyAreasNearbyData.data[`unitNearBy-${n.nearby.id}`] = [
        this.propertyAreasNearbyData.data[`unitNearBy-${n.nearby.id}`]
          ? this.propertyAreasNearbyData.data[`unitNearBy-${n.nearby.id}`]
          : this.dataToFormInit.units[0].id,
      ];

      this.propertyAreasNearbyData.data[`hideNearby${n.nearby.id}`] = [
        this.propertyAreasNearbyData.data[`hideNearby${n.nearby.id}`] === true
          ? this.propertyAreasNearbyData.data[`hideNearby${n.nearby.id}`]
          : false,
      ];
    }
  }

  private applyDivisionsMinRule() {
    if (
      !this.propertyAreasNearbyForm ||
      !this.dataToFormInit?.divisions?.length
    )
      return;

    const ruleKey = "areasNearby.divisions";
    const ids = this.dataToFormInit.divisions.map((d: any) => d.division.id);

    const validator = (group: AbstractControl) => {
      const enabled = this.propertyValidationService.isEnabled(ruleKey);
      if (!enabled) return null;

      const isApartmentOrHouse = this.dataToFormInitDetails?.propertyTypes?.find(
        (s: any) => {
          const idToCompare = this.detailsForm?.data?.propertyType2
            ? this.detailsForm?.data?.propertyType2
            : this.detailsForm?.data?.propertyType;
          return s.id === idToCompare &&
            (s?.groupMainDescription === "Apartments" || s?.groupMainDescription === "Houses" || s?.groupMainDescription === "---");
        },
      );
      if (!isApartmentOrHouse) return null;

      const min = this.propertyValidationService.minValue(ruleKey);

      const filledCount = ids.reduce((acc: any, id: number) => {
        const v = Number((group as FormGroup).get(`division${id}`)?.value ?? 0);
        return acc + (v > 0 ? 1 : 0);
      }, 0);

      return filledCount >= min ? null : { warning: true };
    };

    this.propertyAreasNearbyForm.addValidators(validator);
    this.propertyAreasNearbyForm.updateValueAndValidity({ emitEvent: false });
  }

  getFormErrors(): any[] {
    const errors: any[] = [];

    const groupErrors = this.propertyAreasNearbyForm.errors;

    if (groupErrors?.["warning"]) {
      errors.push({ key: "divisions", type: { warning: true } });
    }

    Object.keys(this.propertyAreasNearbyForm.controls).forEach((key) => {
      const ctrl = this.propertyAreasNearbyForm.get(key);
      const type = ctrl?.errors;

      if (!type) return;

      if (key.startsWith("division") || key.startsWith("hideDivision")) return;
      if (key.startsWith("nearby") || key.startsWith("hideNearby")) return;

      errors.push({ key, type });
    });

    return errors;
  }

  increment(division: any) {
    let val = this.propertyAreasNearbyForm.get(`division${division.id}`)?.value;
    if (val < 300) {
      val++;
      const data1: any = {};
      data1[`division${division.id}`] = val;
      this.propertyAreasNearbyForm.patchValue(data1);
      this.propertyAreasNearbyForm.addControl(
        `areaInput-${division.id}-${val}`,
        this.formBuilder.control(""),
      );
      const data2: any = {};
      data2[`areaInput-${division.id}-${val}`] = 0;
      this.propertyAreasNearbyForm.patchValue(data2);
    }
    for (let i = 0; i < val; i++) { }
  }

  decrement(division: any) {
    let val = this.propertyAreasNearbyForm.get(`division${division.id}`)?.value;
    if (val > 0) {
      this.propertyAreasNearbyForm.removeControl(
        `areaInput-${division.id}-${val}`,
      );
      val--;
      const data1: any = {};
      data1[`division${division.id}`] = val;
      this.propertyAreasNearbyForm.patchValue(data1);
    }
  }

  deleteAreaDivision(division: any, areaInput: any) {
    let data: any = [];
    let val = this.propertyAreasNearbyForm.get(`division${division.id}`)?.value;
    for (let i = 1; i <= val; i++) {
      const val2 = this.propertyAreasNearbyForm.get(
        `areaInput-${division.id}-${i}`,
      )?.value;
      if (`areaInput-${division.id}-${i}` !== areaInput) {
        data.push(val2);
      }
      this.propertyAreasNearbyForm.removeControl(
        `areaInput-${division.id}-${i}`,
      );
    }
    val--;
    if (val === 0) {
      const data1: any = {};
      data1[`division${division.id}`] = val;
      this.propertyAreasNearbyForm.patchValue(data1);
    }
    for (let i = 0; i < data.length; i++) {
      const data1: any = {};
      data1[`division${division.id}`] = val;
      this.propertyAreasNearbyForm.patchValue(data1);
      this.propertyAreasNearbyForm.addControl(
        `areaInput-${division.id}-${i + 1}`,
        this.formBuilder.control(""),
      );
      const data2: any = {};
      data2[`areaInput-${division.id}-${i + 1}`] = data[i];
      this.propertyAreasNearbyForm.patchValue(data2);
    }
  }

  isVisibility(nerbay: any) {
    return false;
  }

  setConfig() {
    if (
      !this.permissionsService.checkMasterPermission(
        "EDIT_PROPERTIES",
        this.property?.store?.id,
      ) &&
      !this.permissionsService.checkPropertyAgentPermission(
        this.property,
        "EDIT_PROPERTIES",
      ) &&
      this.property
    ) {
      if (this.formElementRef.nativeElement) {
        this.renderer.setStyle(
          this.formElementRef.nativeElement,
          "pointer-events",
          "none",
        );
        const parentElement = this.formElementRef.nativeElement.parentElement;
        if (parentElement)
          this.renderer.setStyle(parentElement, "cursor", "not-allowed");
      }
    }
  }

  ngOnDestroy(): void {
    this.propertyAreasNearbyData.data = this.propertyAreasNearbyForm.value;
    this.propertyAreasNearbyData.errors = this.getFormErrors();
    this.propertyAreasNearbyData.valid = this.propertyAreasNearbyForm.valid;
    this.subscriptionSubmitted.unsubscribe();
    this.aiExtractedSub?.unsubscribe();
  }
}
